import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check, ShoppingCart } from "lucide-react";
import { api, money } from "../lib/api";
import { track } from "../lib/analytics";
import { useCart } from "../context/CartContext";
import Seo from "../components/Seo";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [nf, setNf] = useState(false);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setP(null); setNf(false);
    api.get(`/products/${slug}`).then(({ data }) => { setP(data); track("product_view", { product: data.name }); }).catch(() => setNf(true));
  }, [slug]);

  if (nf) return <div className="pt-40 pb-32 text-center"><h1 className="font-serif text-4xl text-brand-green">Ürün bulunamadı</h1><Link to="/magaza" className="text-brand-gold mt-4 inline-block">Mağaza</Link></div>;
  if (!p) return <div className="pt-40 pb-40 text-center text-brand-text/50">Yükleniyor…</div>;

  const price = p.sale_price || p.price;
  const jsonLd = { "@context": "https://schema.org", "@type": "Product", name: p.name, image: p.cover, description: p.short_description,
    offers: { "@type": "Offer", price: price, priceCurrency: "TRY", availability: "https://schema.org/InStock" } };

  const addToCart = () => { add(p, qty); toast.success("Sepete eklendi."); };
  const buyNow = () => { add(p, qty); nav("/sepet"); };

  return (
    <>
      <Seo title={p.seo_title || p.name} description={p.seo_description || p.short_description} image={p.cover} type="product" jsonLd={jsonLd} />
      <section className="pt-32 pb-20 bg-brand-offwhite">
        <div className="container-narrow px-6 lg:px-10 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="rounded-sm overflow-hidden border border-brand-gold-light/40">
              <img src={p.cover} alt={p.name} className="w-full h-[420px] object-cover" />
            </div>
          </div>
          <div>
            <Link to="/magaza" className="text-brand-text/50 text-sm hover:text-brand-green">← Mağaza</Link>
            <span className="block text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mt-4">{p.category}</span>
            <h1 className="font-serif text-4xl md:text-5xl font-light text-brand-green mt-2 break-words">{p.name}</h1>
            <p className="text-brand-text/75 text-lg mt-4 leading-relaxed">{p.short_description}</p>
            <div className="mt-6 flex items-center gap-3">
              <span className="font-serif text-3xl text-brand-green">{money(price)}</span>
              {p.sale_price && <span className="text-brand-text/40 line-through">{money(p.price)}</span>}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              {p.product_type === "physical" && (
                <div className="flex items-center border border-brand-gold-light/50 rounded-sm">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-2.5 text-brand-green" data-testid="qty-minus">−</button>
                  <span className="px-4 min-w-[3rem] text-center" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty(qty + 1)} className="px-4 py-2.5 text-brand-green" data-testid="qty-plus">+</button>
                </div>
              )}
              <button onClick={addToCart} data-testid="add-to-cart" className="inline-flex items-center gap-2 bg-brand-gold text-brand-offwhite hover:bg-brand-green transition-colors px-6 py-3 rounded-sm font-semibold"><ShoppingCart size={18} /> Sepete Ekle</button>
              <button onClick={buyNow} data-testid="buy-now" className="border border-brand-gold text-brand-green hover:bg-brand-gold hover:text-brand-offwhite transition-colors px-6 py-3 rounded-sm font-semibold">Hemen Al</button>
            </div>
            {p.long_description && <p className="text-brand-text/70 mt-8 leading-relaxed">{p.long_description}</p>}
          </div>
        </div>
      </section>

      <section className="section-pad bg-brand-cream">
        <div className="container-narrow grid md:grid-cols-3 gap-10">
          {p.contains?.length > 0 && <div><h3 className="font-serif text-2xl text-brand-green mb-4">Ne içerir?</h3><ul className="space-y-2">{p.contains.map((c, i) => <li key={i} className="flex gap-2 text-brand-text/80 text-sm"><Check size={16} className="text-brand-gold mt-0.5 shrink-0" />{c}</li>)}</ul></div>}
          {p.for_whom?.length > 0 && <div><h3 className="font-serif text-2xl text-brand-green mb-4">Kimler için?</h3><ul className="space-y-2">{p.for_whom.map((c, i) => <li key={i} className="flex gap-2 text-brand-text/80 text-sm"><Check size={16} className="text-brand-gold mt-0.5 shrink-0" />{c}</li>)}</ul></div>}
          {p.usage?.length > 0 && <div><h3 className="font-serif text-2xl text-brand-green mb-4">Kullanım önerileri</h3><ul className="space-y-2">{p.usage.map((c, i) => <li key={i} className="flex gap-2 text-brand-text/80 text-sm"><Check size={16} className="text-brand-gold mt-0.5 shrink-0" />{c}</li>)}</ul></div>}
        </div>
      </section>
    </>
  );
}
